import { useCallback, useEffect, useRef, type ReactNode } from 'react'

/**
 * Cloudflare Turnstile widget for the admin SPA — Vite port of
 * `apps/dashboard/src/components/turnstile.tsx`.
 *
 * Supabase CAPTCHA protection is enabled project-wide, so GoTrue rejects the
 * password grant with `captcha protection: request disallowed (no
 * captcha_token found)` unless the browser forwards a solved token. GoTrue runs
 * the siteverify itself; this widget only produces the token.
 *
 * The widget renders only when a site key is configured, so environments
 * without CAPTCHA protection keep their current behaviour.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string
          callback?: (token: string) => void
          'expired-callback'?: () => void
          'error-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
          size?: 'normal' | 'compact'
        },
      ) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
    onTurnstileLoad?: () => void
  }
}

export const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad'

/**
 * Site key for the admin app.
 *
 * `vite.config.ts` sets `envPrefix: ['VITE_', 'NEXT_PUBLIC_']`, so the shared
 * monorepo `NEXT_PUBLIC_TURNSTILE_SITE_KEY` is inlined alongside a native
 * `VITE_TURNSTILE_SITE_KEY` — the same precedence `auth-redirect.ts` uses.
 *
 * Read at call time rather than module scope so callers (and tests) can vary it.
 */
export function getTurnstileSiteKey(): string | undefined {
  const key: unknown =
    import.meta.env.VITE_TURNSTILE_SITE_KEY ??
    import.meta.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

  return typeof key === 'string' && key.length > 0 ? key : undefined
}

interface TurnstileProps {
  readonly onVerify: (token: string) => void
  readonly onExpire?: (() => void) | undefined
  readonly onError?: (() => void) | undefined
  readonly theme?: 'light' | 'dark' | 'auto'
  readonly size?: 'normal' | 'compact'
  readonly className?: string
  readonly siteKey?: string | undefined
}

export function Turnstile({
  onVerify,
  onExpire,
  onError,
  theme = 'auto',
  size = 'normal',
  className,
  siteKey,
}: TurnstileProps): ReactNode {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const scriptLoadedRef = useRef(false)

  // Callbacks live in a ref rather than in the render effect's dependencies: the
  // forms pass inline arrows, so their identity changes on every keystroke and
  // would otherwise force the effect to remove + recreate the widget mid-typing.
  const callbacksRef = useRef({ onVerify, onExpire, onError })
  useEffect(() => {
    callbacksRef.current = { onVerify, onExpire, onError }
  })

  const resolvedSiteKey = siteKey ?? getTurnstileSiteKey()

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || !resolvedSiteKey) return

    if (widgetIdRef.current) {
      try {
        window.turnstile.remove(widgetIdRef.current)
      } catch {
        // Widget may already be removed.
      }
    }

    containerRef.current.innerHTML = ''

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: resolvedSiteKey,
      callback: (token: string) => callbacksRef.current.onVerify(token),
      'expired-callback': () => callbacksRef.current.onExpire?.(),
      'error-callback': () => callbacksRef.current.onError?.(),
      theme,
      size,
    })
  }, [resolvedSiteKey, theme, size])

  useEffect(() => {
    if (!resolvedSiteKey) {
      console.warn('TURNSTILE site key not configured; CAPTCHA widget disabled')
      return
    }

    let cancelled = false

    if (window.turnstile) {
      renderWidget()
    } else if (!scriptLoadedRef.current) {
      scriptLoadedRef.current = true

      const script = document.createElement('script')
      script.src = TURNSTILE_SCRIPT_SRC
      script.async = true
      script.defer = true

      window.onTurnstileLoad = () => {
        if (!cancelled) renderWidget()
      }

      document.head.appendChild(script)
    }

    return () => {
      cancelled = true

      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          // Widget may already be removed.
        }
        widgetIdRef.current = null
      }
    }
  }, [resolvedSiteKey, renderWidget])

  if (!resolvedSiteKey) return null

  return (
    <div
      ref={containerRef}
      className={className}
      data-testid="turnstile-widget"
    />
  )
}
